import { type ProblemType } from './level';

export interface World {
  readonly id: number;
  readonly name: string;
  readonly description: string;
  readonly problemType: ProblemType;
  readonly totalLevels: number;
  readonly themeId: string;
}

export const WORLDS: readonly World[] = [
  {
    id: 1,
    name: 'Backstreet Routes',
    description:
      "Smuggle supplies through Gearfall's underbelly — find the safest paths through back alleys and steam tunnels.",
    problemType: 'shortestPathPlanar',
    totalLevels: 20,
    themeId: 'gearfall',
  },
  {
    id: 2,
    name: 'Running the Blockade',
    description:
      'Tether every skyport into one rigging network across the open skies — dodging Council patrols between the floating islands.',
    problemType: 'mst',
    totalLevels: 10,
    themeId: 'blockade',
  },
  {
    id: 3,
    name: "The People's Grid",
    description:
      "Rekka runs the copper rounds — hit every substation in the People's Grid exactly once and return to base by the shortest route.",
    problemType: 'tsp',
    totalLevels: 8,
    themeId: 'coppergrid',
  },
  {
    id: 4,
    name: 'Flood the Lines',
    description: 'Hijack aether conduits and redirect maximum energy to blacked-out districts.',
    problemType: 'maxFlow',
    totalLevels: 8,
    themeId: 'aether',
  },
  {
    id: 5,
    name: 'Cut the Cord',
    description: 'Sever the minimum conduits to isolate Council military HQ from its power supply.',
    problemType: 'minCut',
    totalLevels: 8,
    themeId: 'severance',
  },
  {
    id: 6,
    name: 'Safe Houses',
    description: 'Assign operatives to cover identities and safe houses across Gearfall.',
    problemType: 'matching',
    totalLevels: 8,
    themeId: 'undercity',
  },
  {
    id: 7,
    name: 'Counter-Sabotage',
    description:
      "Sabotage Council networks while defending your own in the final assault on Varga's stronghold.",
    problemType: 'interdiction',
    totalLevels: 8,
    themeId: 'stronghold',
  },
];

export function worldById(id: number): World | undefined {
  return WORLDS.find((w) => w.id === id);
}

export function levelId(worldIndex: number, levelIndex: number): string {
  return `w${worldIndex}_l${String(levelIndex).padStart(2, '0')}`;
}

export function levelAssetPath(worldIndex: number, levelIndex: number): string {
  return `levels/world_${worldIndex}/level_${String(levelIndex).padStart(2, '0')}.json`;
}

export function problemTypeLabel(type: ProblemType): string {
  switch (type) {
    case 'shortestPath':
    case 'shortestPathPlanar':
      return 'Shortest Path';
    case 'mst':
      return 'Minimum Spanning Tree';
    case 'maxFlow':
      return 'Maximum Flow';
    case 'minCut':
      return 'Minimum Cut';
    case 'matching':
      return 'Bipartite Matching';
    case 'interdiction':
      return 'Network Interdiction';
    case 'tsp':
      return 'Travelling Salesman';
  }
}

export function problemTypeRule(type: ProblemType): string {
  switch (type) {
    case 'shortestPath':
    case 'shortestPathPlanar':
      return 'Select roads to build one path from the source to the sink. Lower total weight is better.';
    case 'mst':
      return 'Connect every node into one network without closing a loop. Lower total weight is better.';
    case 'maxFlow':
      return 'Trace a route from the source to the sink, pipe by pipe. Reaching the sink opens the valves as far as the narrowest pipe allows. Higher total is better.';
    case 'minCut':
      return 'Cut the cheapest set of edges that separates source from sink. Lower total capacity is better.';
    case 'matching':
      return 'Pair up nodes so that no node is used twice. More pairs are better.';
    case 'interdiction':
      return 'Remove edges within your budget to make the enemy route as long as possible. Higher is better.';
    case 'tsp':
      return 'Visit every node exactly once and return to the start. Lower total weight is better.';
  }
}

// Fronts whose boards are not redrawn yet stay closed until they are.
const OPEN_WORLD_IDS: ReadonlySet<number> = new Set([1, 3]);

export function isWorldOpen(worldId: number): boolean {
  return OPEN_WORLD_IDS.has(worldId);
}

export const OPEN_WORLDS: readonly World[] = WORLDS.filter((world) => isWorldOpen(world.id));
